if (!customElements.get('niihai-product-navigation')) {
  customElements.define('niihai-product-navigation', class extends HTMLElement {
    async connectedCallback() {
      const context = this.getAttribute('data-context-url');
      if (!context || this.querySelector('a')) return;
      this.controller?.abort();
      this.controller = new AbortController();
      const url = new URL(context, window.location.origin);
      if (url.origin !== window.location.origin) return;
      try {
        let previousPageLast = null;
        let pageNumber = 1;
        // Use the collection's actual sequence, including page boundaries.
        while (this.isConnected) {
          const page = await this.loadPage(url, pageNumber);
          if (!page) return;
          const nextPage = page.nextPage;
          const index = page.products.findIndex(item => item.handle === this.dataset.productHandle);
          if (index !== -1) {
            const previous = page.products[index - 1] || previousPageLast;
            let next = page.products[index + 1];
            if (!next && nextPage) {
              const followingPage = await this.loadPage(url, nextPage);
              next = followingPage?.products[0];
            }
            const links = [this.createLink(previous, 'previous', url), this.createLink(next, 'next', url)].filter(Boolean);
            if (this.isConnected) this.replaceChildren(...links);
            return;
          }
          if (!nextPage) return;
          previousPageLast = page.products.at(-1);
          pageNumber = nextPage;
        }
      } catch (error) {
        // Navigation is optional; the PDP must remain usable if it cannot load.
      }
    }
    async loadPage(context, pageNumber) {
      const url = new URL(context);
      url.searchParams.set('section_id', 'product-navigation');
      url.searchParams.set('page', pageNumber);
      const response = await fetch(url, {signal: this.controller.signal});
      if (!response.ok) return null;
      const page = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('[data-product-navigation-page]');
      if (!page) return null;
      return {products: JSON.parse(page.querySelector('[data-product-sequence]').textContent), nextPage: Number(page.dataset.nextPage)};
    }
    createLink(product, direction, context) {
      if (!product) return null;
      const link = this.querySelector(`[data-product-nav-${direction}]`).content.querySelector('a').cloneNode(true);
      const url = new URL(product.url, window.location.origin);
      if (url.origin !== window.location.origin) return null;
      if (context.searchParams.has('sort_by')) url.searchParams.set('sort_by', context.searchParams.get('sort_by'));
      link.href = url.pathname + url.search;
      link.setAttribute('aria-label', `${direction === 'previous' ? 'Previous' : 'Next'} product: ${product.title}`);
      return link;
    }
    disconnectedCallback() { this.controller?.abort(); }
  });
}

if (!customElements.get('niihai-sticky-purchase')) {
  customElements.define('niihai-sticky-purchase', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.gallery = this.closest('.shopify-section')?.querySelector('product-gallery');
      this.form = document.getElementById(this.getAttribute('form'));
      if (!this.gallery || !this.form) return;
      this.mobile = matchMedia('(max-width: 999px)');
      this.mobileAction = this.querySelector('[data-sticky-mobile-action]');
      if (!this.form.niihaiStickyChoice) {
        this.form.niihaiStickyChoice = {
          pending: false,
          adding: false
        };
      }
      this.choice = this.form.niihaiStickyChoice;
      this.footer = document.querySelector('.shopify-section--footer');
      this.observer = new IntersectionObserver(() => this.syncVisibility());
      this.observer.observe(this.gallery);
      this.observer.observe(this.form);
      if (this.footer) this.observer.observe(this.footer);
      this.mobile.addEventListener('change', () => this.syncVisibility(), {signal: this.controller.signal});
      this.addEventListener('click', event => {
        if (event.target.closest('[data-sticky-mobile-action]')) {
          this.submitMobile();
          return;
        }
        const mobileSize = event.target.closest('[data-sticky-mobile-value]');
        if (mobileSize) {
          this.selectMobileSize(mobileSize);
          return;
        }
        const button = event.target.closest('[data-sticky-option-value]');
        if (button && this.contains(button)) {
          this.selectSize(button);
        }
      }, {signal: this.controller.signal});
      this.form.addEventListener('variant:change', () => this.syncMobileAction(), {signal: this.controller.signal});
      this.form.addEventListener('variant:add', () => {
        this.choice.adding = false;
        this.syncMobileAction();
      }, {signal: this.controller.signal});
      this.form.addEventListener('cart:error', event => {
        this.choice.adding = false;
        this.showMobileError(event.detail?.error || 'Could not add to bag. Please try again.');
        this.syncMobileAction();
      }, {signal: this.controller.signal});
      this.resizeObserver = new ResizeObserver(() => this.syncHeight());
      this.resizeObserver.observe(this);
      this.syncMobileAction();
      this.syncVisibility();
    }
    syncVisibility() {
      const form = this.form.getBoundingClientRect();
      const gallery = this.gallery.getBoundingClientRect();
      const footer = this.footer?.getBoundingClientRect();
      const formVisible = form.top < innerHeight && form.bottom > 0;
      const galleryVisible = gallery.top < innerHeight && gallery.bottom > 0;
      const footerVisible = footer && footer.top < innerHeight && footer.bottom > 0;
      this.hidden = footerVisible || formVisible || !(form.bottom <= 0 || (this.mobile.matches && galleryVisible));
      this.inert = this.hidden;
      document.body.toggleAttribute('data-niihai-sticky-purchase', !this.hidden);
      if (!this.hidden) {
        this.querySelectorAll('.niihai-sticky-purchase__sizes').forEach(list => {
          const selected = list.querySelector('[aria-pressed="true"]');
          if (!selected) return;
          const bounds = list.getBoundingClientRect();
          const item = selected.getBoundingClientRect();
          if (item.right > bounds.right) list.scrollLeft += item.right - bounds.right;
          else if (item.left < bounds.left) list.scrollLeft += item.left - bounds.left;
        });
      }
      this.syncHeight();
    }
    syncHeight() {
      if (!this.hidden) {
        document.body.style.setProperty('--niihai-sticky-purchase-height', `${this.getBoundingClientRect().height}px`);
      }
    }
    syncMobileAction() {
      if (!this.mobileAction) return;
      const label = this.mobileAction.dataset.addLabel;
      this.querySelector('[data-sticky-mobile-label]').textContent = label;
      this.mobileAction.disabled = this.mobileAction.hasAttribute('data-unavailable') || this.choice.pending || this.choice.adding;
      this.mobileAction.setAttribute('aria-busy', String(this.choice.pending || this.choice.adding));
      this.querySelectorAll('[data-sticky-mobile-value]').forEach(button => {
        button.setAttribute('aria-pressed', String(button.dataset.current === 'true'));
      });
    }
    showMobileError(message) {
      const status = this.querySelector('[data-sticky-status]');
      status.textContent = message;
      status.hidden = false;
    }
    async selectMobileSize(button) {
      if (button.disabled || this.choice.pending || this.choice.adding) return;
      const formId = this.form.getAttribute('id');
      const section = this.closest('.shopify-section');
      const picker = [...section.querySelectorAll('variant-picker')].find(element => element.getAttribute('form-id') === formId);
      const input = [...this.form.elements].find(element => element.matches('input[data-option-position]') && element.dataset.optionPosition === button.dataset.stickyOptionPosition && element.value === button.dataset.stickyMobileValue);
      if (!picker || !input) {
        this.showMobileError('Could not select size. Please try again.');
        return;
      }
      this.choice.pending = true;
      this.syncMobileAction();
      const status = this.querySelector('[data-sticky-status]');
      status.hidden = true;
      try {
        if (!input.checked) {
          const inputs = [input, ...[...this.form.elements].filter(element => element.matches('input[data-option-position]:checked') && element.name !== input.name)];
          const optionValues = inputs.sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition)).map(element => element.value);
          // Keep the sticky selection, PDP price and product form in sync.
          await picker.selectCombination({optionValues, productChange: input.hasAttribute('data-product-url')});
        }
        this.choice.pending = false;
        const current = [...section.querySelectorAll('niihai-sticky-purchase')].find(element => element.getAttribute('form') === formId);
        if (!current) return;
        current.syncMobileAction();
      } catch {
        this.choice.pending = false;
        const current = [...section.querySelectorAll('niihai-sticky-purchase')].find(element => element.getAttribute('form') === formId);
        current?.showMobileError('Could not select size. Please try again.');
        current?.syncMobileAction();
      }
    }
    submitMobile() {
      if (this.choice.pending || this.choice.adding || this.mobileAction.hasAttribute('data-unavailable')) return;
      if (!this.form.reportValidity()) return;
      this.choice.adding = true;
      this.querySelector('[data-sticky-status]').hidden = true;
      this.syncMobileAction();
      this.form.requestSubmit();
    }
    async selectSize(button) {
      if (button.disabled || this.hasAttribute('aria-busy') || button.getAttribute('aria-pressed') === 'true') return;
      const picker = [...this.closest('.shopify-section').querySelectorAll('variant-picker')].find(element => element.getAttribute('form-id') === this.form.getAttribute('id'));
      const input = [...this.form.elements].find(element => element.matches('input[data-option-position]') && element.dataset.optionPosition === button.dataset.stickyOptionPosition && element.value === button.dataset.stickyOptionValue);
      if (!picker || !input) return;
      const inputs = [input, ...[...this.form.elements].filter(element => element.matches('input[data-option-position]:checked') && element.name !== input.name)];
      const optionValues = inputs.sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition)).map(element => element.value);
      const buttons = [...this.querySelectorAll('button')].map(element => [element, element.disabled]);
      const status = this.querySelector('[data-sticky-status]');
      status.hidden = true;
      this.setAttribute('aria-busy', 'true');
      buttons.forEach(([element]) => { element.disabled = true; });
      try {
        // The native picker updates the URL, form, prices and all product blocks together.
        await picker.selectCombination({optionValues, productChange: input.hasAttribute('data-product-url')});
      } catch {
        if (this.isConnected) {
          status.textContent = 'Could not update size. Please try again.';
          status.hidden = false;
        }
      } finally {
        if (this.isConnected) {
          this.removeAttribute('aria-busy');
          buttons.forEach(([element, disabled]) => { element.disabled = disabled; });
        }
      }
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      this.controller?.abort();
      document.body.removeAttribute('data-niihai-sticky-purchase');
      document.body.style.removeProperty('--niihai-sticky-purchase-height');
    }
  });
}

if (!customElements.get('niihai-cart-klarna')) {
  customElements.define('niihai-cart-klarna', class extends HTMLElement {
    static observedAttributes = ['amount', 'enabled'];
    static sdkReady;

    static getLocale(country, currency, language) {
      const markets = {
        AT: ['EUR', 'de'], AU: ['AUD'], BE: ['EUR', 'nl', 'fr'], CA: ['CAD', 'fr'],
        CH: ['CHF', 'de', 'fr', 'it'], CZ: ['CZK', 'cs'], DE: ['EUR', 'de'], DK: ['DKK', 'da'],
        ES: ['EUR', 'es'], FI: ['EUR', 'fi', 'sv'], FR: ['EUR', 'fr'], GB: ['GBP'],
        GR: ['EUR', 'el'], HU: ['HUF', 'hu'], IE: ['EUR'], IT: ['EUR', 'it'],
        MX: ['MXN', 'es'], NL: ['EUR', 'nl'], NO: ['NOK', 'no', 'nb'], NZ: ['NZD'],
        PL: ['PLN', 'pl'], PT: ['EUR', 'pt'], RO: ['RON', 'ro'], SE: ['SEK', 'sv'],
        SK: ['EUR', 'sk'], US: ['USD', 'es']
      };
      const market = markets[country];
      if (!market || market[0] !== currency) return null;
      const primary = (language || 'en').split('-')[0];
      return `${market.slice(1).includes(primary) ? primary : 'en'}-${country}`;
    }

    static loadSdk(clientId) {
      if (customElements.get('klarna-placement')) return Promise.resolve();
      if (!this.sdkReady) {
        this.sdkReady = new Promise((resolve, reject) => {
          let script = document.querySelector('script[src="https://js.klarna.com/web-sdk/v1/klarna.js"]');
          if (!script) {
            script = document.createElement('script');
            script.src = 'https://js.klarna.com/web-sdk/v1/klarna.js';
            script.async = true;
            script.id = 'klarna-osm-script-tag';
            script.dataset.clientId = clientId;
            script.dataset.environment = 'production';
            document.head.append(script);
          }
          script.addEventListener('error', reject, {once: true});
          customElements.whenDefined('klarna-placement').then(resolve);
        });
      }
      return this.sdkReady;
    }

    constructor() {
      super();
      // Isolate the cart total from the Shopify app's product-price observer.
      const root = this.attachShadow({mode: 'open'});
      const style = document.createElement('style');
      style.textContent = `
        :host { display: block; }
        :host([hidden]) { display: none; }
        :host([data-ready]) { margin: 12px 16px 16px; text-align: left; }
        klarna-placement { display: block; }
        klarna-placement::part(osm-container) { border: 0; padding: 0; background: transparent; text-align: left; }
        klarna-placement::part(osm-message), klarna-placement::part(osm-cta) {
          font-family: Arial, sans-serif; font-size: 12px; line-height: 1.4; color: #333; text-align: left;
        }
        klarna-placement::part(osm-legal) { font-size: 10px; line-height: 1.4; color: #666; text-align: left; }
      `;
      root.append(style);
    }

    connectedCallback() {
      if (this.placement) this.resizeObserver?.observe(this.placement);
      this.update();
    }
    attributeChangedCallback() { if (this.isConnected) this.update(); }
    disconnectedCallback() { this.resizeObserver?.disconnect(); }

    async update() {
      const amount = Number(this.getAttribute('amount'));
      const locale = this.constructor.getLocale(this.getAttribute('country'), this.getAttribute('currency'), this.getAttribute('language'));
      if (!Number.isSafeInteger(amount) || amount <= 0 || !locale) {
        this.hidden = true;
        return;
      }
      if (!this.hasAttribute('enabled')) return;
      try {
        await this.constructor.loadSdk(this.getAttribute('client-id'));
        if (!this.isConnected || Number(this.getAttribute('amount')) !== amount) return;
        this.hidden = false;
        if (!this.placement) {
          this.placement = document.createElement('klarna-placement');
          this.placement.dataset.key = 'credit-promotion-auto-size';
          this.placement.dataset.theme = 'default';
          this.placement.dataset.locale = locale;
          this.placement.dataset.purchaseAmount = String(amount);
          this.shadowRoot.append(this.placement);
          this.resizeObserver = new ResizeObserver(() => {
            this.toggleAttribute('data-ready', this.placement.getBoundingClientRect().height > 0);
          });
          this.resizeObserver.observe(this.placement);
        } else if (this.placement.dataset.purchaseAmount !== String(amount)) {
          this.placement.dataset.purchaseAmount = String(amount);
        }
      } catch (_) {
        this.hidden = true;
      }
    }
  });
}
